import { colors, catalogColors as COLORS } from '../theme';
import React, { useEffect, useRef, useState } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Paper,
  Drawer,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  Button,
  ListItemText,
  Box,
  Divider,
  useMediaQuery,
  useTheme,
  Alert,
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import DeleteIcon from '@mui/icons-material/Delete';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useCart } from '../context/CartContext';
import { buildWhatsAppUrl } from '../config/site';
import {
  formatPrice,
  getProductDisplayName,
  getProductImageSizes,
  getProductImageSrcSet,
  getProductImageUrl,
} from '../utils/product';
import { buildCartWhatsAppMessage, CART_ISSUE_MESSAGES, getCartPrice, getCartSummary } from '../utils/cart';


const Cart = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isBumping, setIsBumping] = useState(false);
  const sendingRef = useRef(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const open = Boolean(anchorEl);

  const { cart, removeFromCart, clearCart, cartPulseKey, revalidateCart, validating, validationError, adjustQuantityToStock } = useCart();

  useEffect(() => {
    if (!cartPulseKey) return;

    setIsBumping(true);
    const timeout = setTimeout(() => setIsBumping(false), 320);

    return () => clearTimeout(timeout);
  }, [cartPulseKey]);

  const summary = getCartSummary(cart);
  const hasIssues = cart.some(item => item.issue);

  const totalItems = cart.reduce(
    (acc, item) => acc + Number(item.quantity || 0),
    0
  );

  const handleCartClick = (event) => {
    setAnchorEl(event.currentTarget);
    if (cart.length) revalidateCart({ refresh: true });
  };

  const handleCartClose = () => {
    setAnchorEl(null);
  };

  const handleWhatsAppOrder = async () => {
    if (!cart.length || hasIssues || validating || validationError || sendingRef.current) return;
    sendingRef.current = true;
    // Open during the click so browsers allow the new tab after the async validation.
    const popup = window.open('', '_blank');
    if (popup) popup.opener = null;
    try {
      const result = await revalidateCart({ refresh: true });
      if (!result.ok) { popup?.close(); return; }
      const url = buildWhatsAppUrl(buildCartWhatsAppMessage(result.cart));
      if (popup?.closed) return;
      if (popup) popup.location.replace(url);
      else window.location.assign(url);
      handleCartClose();
    } finally {
      sendingRef.current = false;
    }
  };

  const renderCartContent = ({ mobile = false } = {}) => (
    <Paper
      elevation={0}
      sx={{
        p: mobile ? 2 : 2,
        width: mobile ? '100%' : 380,
        maxWidth: mobile ? '100vw' : '100%',
        boxSizing: 'border-box',
        height: mobile ? 'auto' : 'auto',
        maxHeight: mobile ? '80dvh' : 'calc(100dvh - 96px)',
        backgroundColor: COLORS.bg,
        border: mobile ? 'none' : `1px solid ${COLORS.border}`,
        borderRadius: mobile ? '18px 18px 0 0' : 0,
        boxShadow: mobile ? '0 -18px 40px rgba(0,0,0,0.12)' : '0 18px 40px rgba(0,0,0,0.08)',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      <Box sx={{ flex: '0 0 auto' }}>
        {mobile && (
          <Box
            sx={{
              width: 42,
              height: 4,
              borderRadius: 999,
              backgroundColor: colors.controlBorder,
              mx: 'auto',
              mb: 1.5,
            }}
          />
        )}

        <Typography
          variant="overline"
          sx={{
            color: colors.accent,
            fontWeight: 900,
            letterSpacing: 1.2,
          }}
        >
          CARRITO
        </Typography>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 900,
            color: COLORS.text,
            mb: 0.5,
          }}
        >
          Mi seleccion
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: COLORS.muted,
            mb: 2,
          }}
        >
          Armá tu consulta y enviala por WhatsApp.
        </Typography>

        <Divider sx={{ borderColor: COLORS.border, mb: 1.5 }} />
      </Box>

      {cart.length > 0 ? (
        <>
          <List
            sx={{
              py: 0,
              overflowY: 'auto',
              flex: '1 1 auto',
              minHeight: 96,
              maxHeight: mobile ? 'none' : 360,
              pr: mobile ? 0.5 : 0,
            }}
          >
            {cart.map((item) => (
              <ListItem
                key={item.product._id}
                sx={{
                  px: 0,
                  pr: 6,
                  py: 1.2,
                  alignItems: 'flex-start',
                }}
                secondaryAction={
                  <IconButton
                    aria-label={`Eliminar del carrito ${getProductDisplayName(item.product)}`}
                    onClick={() => removeFromCart(item.product._id)}
                    sx={{
                      minWidth: 44,
                      minHeight: 44,
                      color: colors.text,
                      '&:hover': {
                        color: colors.error,
                        backgroundColor: 'rgba(176,0,32,0.06)',
                      },
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemAvatar>
                  <Avatar
                    alt={getProductDisplayName(item.product)}
                    src={getProductImageUrl(item.product.image, 'thumb')}
                    imgProps={{
                      loading: 'lazy',
                      decoding: 'async',
                      srcSet: getProductImageSrcSet(item.product.image, 'thumb'),
                      sizes: getProductImageSizes('thumb'),
                    }}
                    variant="rounded"
                    sx={{
                      width: mobile ? 50 : 56,
                      height: mobile ? 50 : 56,
                      borderRadius: 1.5,
                      border: `1px solid ${COLORS.border}`,
                      backgroundColor: COLORS.bgSoft,
                    }}
                  />
                </ListItemAvatar>

                <ListItemText
                  sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
                  primary={
                    <Typography
                      sx={{
                        color: COLORS.text,
                        fontWeight: 800,
                        lineHeight: 1.3,
                      }}
                    >
                      {getProductDisplayName(item.product)}
                    </Typography>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="body2" sx={{ color: COLORS.muted }}>
                        Cantidad: {item.quantity}
                      </Typography>
                      <Typography variant="body2" sx={{ color: COLORS.muted }}>
                        {['unavailable', 'stock'].includes(item.issue) ? 'No disponible'
                          : getCartPrice(item.product) === null ? 'Consultar' : `$${formatPrice(getCartPrice(item.product))}`}
                      </Typography>
                      {item.issue && <Typography role="alert" variant="body2" sx={{ color: colors.error, mt: 0.75 }}>
                        {CART_ISSUE_MESSAGES[item.issue]}
                      </Typography>}
                      {['quantity', 'quantityExceeded'].includes(item.issue) && <Button size="small"
                        onClick={() => adjustQuantityToStock(item.product._id)} sx={{ mt: 0.5, minHeight: 44 }}>
                        Ajustar a {item.issue === 'quantity' ? 1 : item.product.stock}
                      </Button>}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>

          <Box sx={{ flex: '0 0 auto' }}>
            <Divider sx={{ borderColor: COLORS.border, my: 1.5 }} />
            {validationError && <Alert severity="warning" sx={{ mb: 1.5 }}>{validationError}</Alert>}
            {(validationError || hasIssues) && <Button disabled={validating} onClick={() => revalidateCart({ refresh: true })} sx={{ mb: 1 }}>
              Volver a revisar
            </Button>}

            <Box
              sx={{
                display: 'flex',
                flexDirection: mobile ? 'column' : 'row',
                gap: 1,
                alignItems: mobile ? 'stretch' : 'end',
                justifyContent: mobile ? 'flex-start' : 'space-between',
                width: '100%',
                minWidth: 0,
                mb: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: COLORS.muted, fontWeight: 800, minWidth: 0 }}
              >
                Total seleccion
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  color: COLORS.text,
                  fontWeight: 900,
                  width: mobile ? '100%' : 'auto',
                  textAlign: mobile ? 'left' : 'right',
                  lineHeight: 1.05,
                  minWidth: 0,
                  maxWidth: '100%',
                  overflow: 'visible',
                  overflowWrap: 'anywhere',
                  fontSize: { xs: '1.15rem', sm: '1.25rem' },
                }}
              >
                {summary.label}
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: COLORS.muted, fontSize: '0.75rem', lineHeight: 1.5, mb: 1.5 }}>
              Te confirmamos la seleccion por WhatsApp antes de coordinar.
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                width: '100%',
              }}
            >
              <Button
                variant="outlined"
                onClick={clearCart}
                fullWidth
                sx={{
                  minHeight: 44,
                  borderColor: COLORS.border,
                  color: COLORS.text,
                  borderRadius: '999px',
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': {
                    borderColor: COLORS.text,
                    backgroundColor: COLORS.bgSoft,
                  },
                }}
              >
                Vaciar
              </Button>

              <Button
                variant="contained"
                startIcon={<WhatsAppIcon />}
                onClick={handleWhatsAppOrder}
                disabled={validating || hasIssues || Boolean(validationError)}
                aria-busy={validating}
                fullWidth
                sx={{
                  minHeight: 44,
                  backgroundColor: COLORS.violet,
                  borderRadius: '999px',
                  textTransform: 'none',
                  fontWeight: 800,
                  '&:hover': {
                    backgroundColor: COLORS.violetSoft,
                  },
                }}
              >
                Consultar seleccion por WhatsApp
              </Button>
            </Box>
          </Box>
        </>
      ) : (
        <Typography variant="body2" sx={{ color: COLORS.muted }}>
          Tu selección está vacía.
        </Typography>
      )}
    </Paper>
  );

  return (
    <Box>
      <Box
        sx={{
          '@keyframes cartBump': {
            '0%': { transform: 'scale(1)' },
            '35%': { transform: 'scale(1.14)' },
            '60%': { transform: 'scale(0.95)' },
            '100%': { transform: 'scale(1)' },
          },
          animation: isBumping ? 'cartBump 320ms ease' : 'none',
          transformOrigin: 'center',
        }}
      >
        <Badge
          badgeContent={totalItems}
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: COLORS.violet,
              color: colors.white,
              fontWeight: 800,
            },
          }}
        >
          <IconButton
            aria-label={`Abrir carrito (${totalItems} ${totalItems === 1 ? 'disco' : 'discos'})`}
            onClick={handleCartClick}
            sx={{
              color: COLORS.text,
              border: `1px solid ${COLORS.border}`,
              backgroundColor: colors.surface,
              '&:hover': {
                borderColor: COLORS.violet,
                backgroundColor: colors.tint,
              },
            }}
          >
            <ShoppingCartIcon />
          </IconButton>
        </Badge>
      </Box>

      {isMobile ? (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={handleCartClose}
          PaperProps={{
            sx: {
              width: '100%',
              maxWidth: '100vw',
              left: 0,
              right: 0,
              m: 0,
              boxSizing: 'border-box',
              backgroundColor: 'transparent',
              boxShadow: 'none',
              overflow: 'hidden',
              overflowX: 'hidden',
              pb: 'calc(16px + env(safe-area-inset-bottom))',
            },
          }}
        >
          {renderCartContent({ mobile: true })}
        </Drawer>
      ) : (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleCartClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {renderCartContent({ mobile: false })}
        </Popover>
      )}
    </Box>
  );
};

export default Cart;
